import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$findMatchingParent} from '@lexical/utils';
import {useCollabId} from 'components/DSYS/CollabProvider';
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  KEY_TAB_COMMAND,
  RangeSelection,
} from 'lexical';
import {useEffect} from 'react';

import {
  $createEnhancedHeadingNode,
  $isEnhancedHeadingNode,
  EnhancedHeadingNode,
} from './EnhancedHeadingNode';
import {setPersistedHeadingCollapsed} from './HeadingCollapsePersistence';

/**
 * Plugin to handle Enter and Tab key behavior in EnhancedHeadingNode
 * - Enter at beginning: Create paragraph above, keep cursor in heading
 * - Enter at end: Create paragraph below, move cursor to it
 * - Enter in middle: Split heading, create new heading below
 * - Tab: Disabled when cursor is in a heading node
 */
export const HeadingEnterPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const collabId = useCollabId();

  // Helper function to check if cursor is in a heading node
  const isCursorInHeadingNode = (selection: any): boolean => {
    if (!selection || !selection.isCollapsed() || !$isRangeSelection(selection)) {
      return false;
    }

    const rangeSelection = selection as RangeSelection;
    const anchorNode = rangeSelection.anchor.getNode();

    // Check if we're directly in a heading node
    if ($isEnhancedHeadingNode(anchorNode)) {
      return true;
    }

    // Walk up the tree to find the heading node
    const headingNode = $findMatchingParent(anchorNode, $isEnhancedHeadingNode);
    return headingNode !== null;
  };

  useEffect(() => {
    const removeEnterListener = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent | null) => {
        const selection = $getSelection();

        if (!selection || !selection.isCollapsed() || !$isRangeSelection(selection)) {
          return false;
        }

        const rangeSelection = selection as RangeSelection;
        const anchorNode = rangeSelection.anchor.getNode();
        const focusNode = rangeSelection.focus.getNode();

        // Find the heading node that contains the selection
        let headingNode: EnhancedHeadingNode | null = null;
        let textNode: any = null;

        // Check if we're directly in a heading node
        if ($isEnhancedHeadingNode(anchorNode)) {
          headingNode = anchorNode;
          textNode = focusNode;
        } else {
          // Walk up the tree to find the heading node
          const foundHeadingNode = $findMatchingParent(anchorNode, $isEnhancedHeadingNode);
          if (foundHeadingNode) {
            headingNode = foundHeadingNode;
            textNode = anchorNode;
          }
        }

        if (!headingNode || !textNode) {
          return false;
        }

        const headingText = headingNode.getTextContent();
        const selectionOffset = rangeSelection.anchor.offset;

        // Check if heading only contains zero-width space (from transformer)
        const isOnlyZeroWidthSpace = headingText === '\u200B';

        // Check if heading ends with zero-width space (common case)
        const endsWithZeroWidthSpace = headingText.endsWith('\u200B');

        // Find the position within the heading's text content
        let globalOffset = 0;
        const headingChildren = headingNode.getChildren();

        for (const child of headingChildren) {
          if (child === textNode) {
            globalOffset += selectionOffset;
            break;
          }
          if (child.getTextContent) {
            globalOffset += child.getTextContent().length;
          }
        }

        editor.update(() => {
          if (!headingNode) {
            return;
          }

          if (globalOffset === 0 && !isOnlyZeroWidthSpace) {
            // Enter at beginning: Create paragraph above
            const paragraphNode = $createParagraphNode();
            paragraphNode.append($createTextNode('\u200B')); // Add zero-width space to prevent transformer interference

            headingNode.insertBefore(paragraphNode);
            // Don't move cursor - keep it in the heading
          } else if (
            globalOffset >= headingText.length ||
            isOnlyZeroWidthSpace ||
            (endsWithZeroWidthSpace && globalOffset >= headingText.length - 1)
          ) {
            // Enter at end OR heading only has zero-width space OR cursor is at end of text before zero-width space: Create paragraph below

            // If heading is collapsed, expand it first
            if (headingNode.getCollapsed()) {
              headingNode.setCollapsed(false);
              setPersistedHeadingCollapsed(collabId, headingNode.getHeadingId(), false);
            }

            // Create paragraph below
            const paragraphNode = $createParagraphNode();
            paragraphNode.append($createTextNode('\u200B')); // Add zero-width space to prevent transformer interference

            headingNode.insertAfter(paragraphNode);
            paragraphNode.selectEnd();
          } else {
            // Enter in middle: Split the heading
            const beforeText = headingText.substring(0, globalOffset);
            const afterText = headingText.substring(globalOffset);

            // Update current heading with text before cursor
            headingNode.clear();
            if (beforeText) {
              headingNode.append($createTextNode(beforeText));
            }

            // Create new heading with text after cursor
            const newHeadingNode = $createEnhancedHeadingNode(
              headingNode.getTag(),
              false,
              headingNode.getSettings()
            );

            if (afterText) {
              newHeadingNode.append($createTextNode(afterText));
            }

            headingNode.insertAfter(newHeadingNode);
            newHeadingNode.selectStart();
          }
        });

        if (event) {
          event.preventDefault();
        }

        return true;
      },
      COMMAND_PRIORITY_HIGH
    );

    const removeTabListener = editor.registerCommand(
      KEY_TAB_COMMAND,
      (event: KeyboardEvent | null) => {
        const selection = $getSelection();

        if (isCursorInHeadingNode(selection)) {
          // Disable Tab key when cursor is in a heading node
          if (event) {
            event.preventDefault();
          }
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      removeEnterListener();
      removeTabListener();
    };
  }, [collabId, editor]);

  return null;
};
